import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { processTerm } from "../../../../api/content/processTerms";
import { listWorkflows, listWorkflowsGiving, type WorkflowListing } from "../../../../api/content/workflowsApi";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { useProjects } from "../../../../projects/projectContext";
import { useLoad } from "../../../../shared/loading/useLoad";
import { appPaths } from "../../../../shared/routing/appPaths";
import { useSubmission } from "../../../../shared/state/useSubmission";
import AppButton from "../../../../ui/buttons/AppButton";
import BlockedState from "../../../../ui/feedback/BlockedState";
import LoadStatus from "../../../../ui/feedback/LoadStatus";
import StatusMessage from "../../../../ui/feedback/StatusMessage";
import ChoiceCard from "../../../../ui/forms/ChoiceCard";
import FieldGroup from "../../../../ui/forms/FieldGroup";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import { releaseDecision } from "../../../knowledgeData/releases/releasePermission";
import type { PreparationViewProps } from "../preparationView";
import { startRun } from "../startCommands";
import type { ReleaseIntent } from "../threadIntent";

/** The core data type a Workflow exports when it makes a release (ADR-0026 slice 3b, ADR-0032). */
const RELEASE_TYPE = processTerm("Release");

/** The listed Workflows module B would start from nothing at hand that give a release or a kind of one. */
async function releasingWorkflows(projectId: string, signal: AbortSignal): Promise<WorkflowListing> {
  const listing = await listWorkflows(projectId, null, signal);
  const giving = await listWorkflowsGiving(projectId, listing.version, RELEASE_TYPE, signal);
  return { ...listing, workflows: listing.workflows.filter((workflow) => giving.has(workflow.reference)) };
}

/**
 * "Release…" (K-12, F2, Johannes 27 Oct): releasing is a Workflow run in a Thread. The view
 * offers the Workflows module B would start that give a release, the DMW library's "Release
 * the knowledge data" first, and Start opens its Thread, whose steps ask what, the metadata
 * and where, then a review before the release is made. Whoever may not release sees why, with
 * BlockedState, and nothing is loaded for them.
 */
export default function ReleasePreparation(props: PreparationViewProps<ReleaseIntent>) {
  return (
    <BlockedState decision={releaseDecision(useProjects(), props.intent.projectId)}>
      <ReleaseChoice {...props} />
    </BlockedState>
  );
}

function ReleaseChoice({ intent, requestKey, onStarted }: PreparationViewProps<ReleaseIntent>) {
  const { t } = useTranslation();
  const { projectId } = intent;
  const load = useCallback((signal: AbortSignal) => releasingWorkflows(projectId, signal), [projectId]);
  const { state: listing, reload } = useLoad(load);
  const [picked, setPicked] = useState<string | null>(null);
  const { state: submission, submit } = useSubmission();

  if (listing.status !== "loaded") return <LoadStatus state={listing} loadingLabel={t("threads.prepare.run.loadingWorkflows")} onRetry={reload} />;
  const releasing = listing.value.workflows;
  if (releasing.length === 0) {
    return (
      <StatusMessage tone="info" title={t("threads.prepare.release.noWorkflowTitle")}>
        <p>{t("threads.prepare.release.noWorkflowBody")}</p>
      </StatusMessage>
    );
  }
  const ordered = [...releasing].sort((left, right) => Number(right.runsInProject) - Number(left.runsInProject));
  const workflow = ordered.find((candidate) => candidate.reference === picked) ?? ordered[0];
  const version = listing.value.version;
  const starting = submission.status === "submitting" || submission.status === "succeeded";
  const start = () => {
    if (workflow === undefined) return;
    submit(async () => {
      const thread = await startRun({
        projectId,
        title: t("threads.prepare.release.threadTitle"),
        workflow: workflow.reference,
        contentVersion: version,
        corpusVersion: null,
        entries: [],
        requestKey,
      });
      onStarted(thread.threadId);
    });
  };

  return (
    <div className="space-y-5">
      <p className={FORM_HELP_TEXT_CLASS}>{t("threads.prepare.release.intro")}</p>
      <FieldGroup legend={t("threads.prepare.run.workflow")} className="space-y-2" disabled={starting}>
        {ordered.map((candidate) => (
          <ChoiceCard
            key={candidate.reference}
            name={`${requestKey}-workflow`}
            checked={workflow?.reference === candidate.reference}
            onChoose={() => { setPicked(candidate.reference); }}
            title={`${candidate.name} · ${t("threads.prepare.run.steps", { count: candidate.stepCount })}`}
            description={candidate.description}
            aside={<Link to={appPaths.workflow(candidate.reference)} className="app-link text-xs">{t("threads.prepare.run.openWorkflow")}</Link>}
          />
        ))}
      </FieldGroup>
      {submission.status === "failed" ? <ErrorNotice error={submission.error} /> : null}
      <div className="flex justify-end">
        <AppButton variant="primary" size="sm" onClick={start} disabled={starting}>
          {starting ? t("threads.prepare.starting") : t("threads.prepare.run.start")}
        </AppButton>
      </div>
    </div>
  );
}
